import { z } from "zod";
import { ProblemDetailsSchema } from "../shared";

// ==================== SCHEMAS ====================

/**
 * Field info data schema
 * Matches API FieldInfoData from GET /field-infos/{entityType}
 */
export const FieldInfoDataSchema = z.object({
	entityType: z.string(),
	propertyName: z.string(),
	label: z.string(),
	required: z.boolean(),
	propertyType: z.string(),
	regex: z.string(),
	maxLength: z.number().int().nullable(),
});

/**
 * Response schema for field info list endpoints
 */
export const FieldInfoDataArraySchema = z.array(FieldInfoDataSchema);

/**
 * Response schema for GET /field-infos/{entityType}/label
 * Returns a single display label string (e.g. "Sub Contractor")
 */
export const FieldInfoLabelSchema = z.string();

/**
 * Response schema for order-bys endpoint (array of strings)
 */
export const FieldInfoOrderBysSchema = z.array(z.string());

/**
 * Problem details schema for error responses
 */
export const FieldInfoProblemDetailsSchema = ProblemDetailsSchema;

// ==================== INFERRED TYPES ====================

export type FieldInfoData = z.infer<typeof FieldInfoDataSchema>;
export type FieldInfoDataList = z.infer<typeof FieldInfoDataArraySchema>;
export type FieldInfoLabel = z.infer<typeof FieldInfoLabelSchema>;
export type FieldInfoOrderBys = z.infer<typeof FieldInfoOrderBysSchema>;
export type FieldInfoProblemDetails = z.infer<
	typeof FieldInfoProblemDetailsSchema
>;

// ==================== PARAM TYPES ====================

/**
 * Query params for GET /field-infos/{entityType} and GET /field-infos/{entityType}/label
 */
export type FieldInfoParams = {
	organizationId?: string;
};

/**
 * Query params for GET /field-infos/localizations
 */
export type FieldInfoLocalizationsParams = {
	organizationId?: string;
	flatten?: boolean;
	forMobile?: boolean;
};

// ==================== ERROR HANDLING ====================

/**
 * Custom error class for Field Info API operations
 */
export class FieldInfoApiError extends Error {
	constructor(
		message: string,
		public error: unknown,
		public code?: string,
		public status?: number,
		public details?: unknown,
	) {
		super(message);
		this.name = "FieldInfoApiError";
	}

	static fromResponse(response: Response, data?: unknown): FieldInfoApiError {
		const message = `API Error: ${response.status} ${response.statusText}`;
		return new FieldInfoApiError(
			message,
			undefined,
			response.statusText,
			response.status,
			data,
		);
	}

	static fromProblemDetails(
		problemDetails: FieldInfoProblemDetails,
	): FieldInfoApiError {
		const message =
			problemDetails.title || problemDetails.detail || "Unknown API error";
		return new FieldInfoApiError(
			message,
			undefined,
			problemDetails.type || undefined,
			problemDetails.status || undefined,
			problemDetails,
		);
	}
}
